import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { CatalogOffering, CatalogService, CatalogWarehouse, InventoryRequisition } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-requisition-detail',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, DialogModule, InputTextModule, TableModule, AinCanAccessDirective],
  template: `
    <ain-page [title]="t('catalog.requisition.detailTitle')" [description]="requisition()?.referenceNumber || ''">
      <ain-inventory-page-hint pageKey="requisitionDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!requisition()) {
        <ain-state type="empty" [title]="t('catalog.requisition.notFound')" />
      } @else {
        <section class="head">
          <a [routerLink]="['/inventory/requisitions']">{{ t('catalog.action.backToList') }}</a>
          <span>{{ t('columns.status') }}: <strong>{{ statusLabel(requisition()!.status) }}</strong></span>
          @if (requisition()!.status === 'Draft') {
            <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.requisitions.submit'" [label]="t('catalog.requisition.submit')" (click)="submit()"></button>
          }
          @if (requisition()!.status === 'Submitted') {
            <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.requisitions.convert'" [label]="t('catalog.requisition.convert')" (click)="convert()"></button>
            <button pButton type="button" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.requisitions.cancel'" [label]="t('catalog.requisition.cancel')" (click)="openCancelDialog()"></button>
          }
          @if (requisition()!.supplyOrderId) {
            <a [routerLink]="['/inventory/supply-orders', requisition()!.supplyOrderId!]">{{ t('catalog.requisition.openSupplyOrder') }}</a>
          }
        </section>

        <p-table [value]="requisition()!.lines">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.inventory.offeringId') }}</th>
              <th>{{ t('catalog.requisition.requestedQty') }}</th>
              <th>{{ t('catalog.requisition.estUnitCost') }}</th>
              <th>{{ t('catalog.requisition.estTotalCost') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-line>
            <tr>
              <td>{{ offeringLabel(line.offeringId) }}</td>
              <td>{{ line.requestedQuantity }}</td>
              <td>{{ line.estimatedUnitCost ?? '—' }} {{ line.currencyCode || '' }}</td>
              <td>{{ line.estimatedTotalCost ?? '—' }}</td>
            </tr>
          </ng-template>
        </p-table>

        @if (requisition()!.cancelReason) {
          <p class="meta">{{ t('catalog.requisition.cancelReason') }}: {{ requisition()!.cancelReason }}</p>
        }
        @if (error()) { <p class="error">{{ error() }}</p> }
      }

      <p-dialog
        [header]="t('catalog.requisition.cancelDialog.title')"
        [visible]="cancelDialogVisible()"
        (visibleChange)="cancelDialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(560px, 92vw)' }">
        <div class="dialog-body">
          <label>{{ t('catalog.requisition.cancelDialog.reasonLabel') }}</label>
          <input pInputText [(ngModel)]="cancelReason" />
          @if (cancelReasonError()) {
            <p class="error">{{ cancelReasonError() }}</p>
          }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeCancelDialog()"></button>
          <button pButton type="button" severity="danger" [label]="t('catalog.requisition.cancel')" [disabled]="cancelBusy()" (click)="confirmCancel()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .head { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 12px; }
    .meta { margin-top: 12px; color: #475569; }
    .error { color: #b91c1c; }
    .dialog-body { display: grid; gap: 8px; }
    .dialog-body label { font-weight: 600; color: #475569; }
  `]
})
export class InventoryRequisitionDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly requisition = signal<InventoryRequisition | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly cancelDialogVisible = signal(false);
  readonly cancelReasonError = signal('');
  readonly cancelBusy = signal(false);
  cancelReason = '';

  t(key: string): string { return this.localization.translate(key); }
  offeringLabel(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : id;
  }

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => void this.load(false)
    );
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => void this.load());
  }

  statusLabel(status: string): string {
    const key = `catalog.requisition.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  submit(): void {
    const id = this.requisition()?.id;
    if (!id) return;
    this.service.submitInventoryRequisition(id).subscribe({
      next: req => this.requisition.set(req),
      error: () => this.error.set(this.t('catalog.requisition.submitFailed'))
    });
  }

  convert(): void {
    const id = this.requisition()?.id;
    if (!id) return;
    const key = `convert-${id}-${Date.now()}`;
    this.service.convertInventoryRequisition(id, key).subscribe({
      next: result => this.requisition.set(result.requisition),
      error: () => this.error.set(this.t('catalog.requisition.convertFailed'))
    });
  }

  openCancelDialog(): void {
    this.cancelReason = '';
    this.cancelReasonError.set('');
    this.cancelDialogVisible.set(true);
  }

  closeCancelDialog(): void {
    this.cancelDialogVisible.set(false);
    this.cancelReasonError.set('');
  }

  confirmCancel(): void {
    const id = this.requisition()?.id;
    if (!id) return;
    if (!this.cancelReason.trim()) {
      this.cancelReasonError.set(this.t('catalog.requisition.cancelDialog.reasonRequired'));
      return;
    }

    this.cancelBusy.set(true);
    this.cancelReasonError.set('');
    this.service.cancelInventoryRequisition(id, this.cancelReason.trim()).subscribe({
      next: req => {
        this.requisition.set(req);
        this.cancelBusy.set(false);
        this.closeCancelDialog();
      },
      error: () => {
        this.cancelBusy.set(false);
        this.error.set(this.t('catalog.requisition.cancelFailed'));
      }
    });
  }

  private load(showLoading = true): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;
    if (showLoading) this.loading.set(true);
    this.service.getInventoryRequisition(id).subscribe({
      next: req => { this.requisition.set(req); this.loading.set(false); },
      error: () => { this.requisition.set(null); this.loading.set(false); }
    });
  }
}
